// Configurable HTTP core for the Bunker REST API.
//
// This is the web-agnostic half of the API layer: it must run unchanged in the
// browser (cookie auth, same-origin relative paths) AND in React Native (bearer
// token, absolute base URL, Hermes runtime). To stay free of `lib.dom` and
// `@types/node` — neither of which `@bunker/shared` pulls in — we declare a
// minimal structural `FetchLike` instead of leaning on the global `fetch`,
// `RequestInit`, or `Response` types, and we base64url-encode the nonce by hand
// rather than via `btoa` (absent in Hermes by default).

import type { ApiErrorBody } from '../types/index.js';

/** The subset of the Fetch `Response` we actually read. */
export interface FetchResponse {
  ok: boolean;
  status: number;
  text(): Promise<string>;
}

/** The subset of `RequestInit` the API layer sets. Open-ended so callers may
 *  pass through extra platform-specific fields. */
export interface FetchRequestInit {
  method?: string;
  // `FormData` for the one multipart endpoint (avatar bytes, contract §2.2).
  // The caller must NOT set Content-Type for it — the runtime writes the
  // boundary, and overriding it produces a body the server cannot parse.
  body?: string | FormData | null;
  headers?: Record<string, string>;
  credentials?: 'include' | 'omit' | 'same-origin';
  signal?: unknown;
  /** Let the request outlive the page (tab close, navigation). */
  keepalive?: boolean;
  [key: string]: unknown;
}

/** A `fetch`-compatible function. Defaults to `globalThis.fetch`. */
export type FetchLike = (url: string, init?: FetchRequestInit) => Promise<FetchResponse>;

/** The bound request function each domain module is built around. */
export type RequestFn = <T>(path: string, init?: FetchRequestInit) => Promise<T>;

export interface ApiClientConfig {
  /**
   * URL prefix prepended to every path. Empty string (the default) yields the
   * web's same-origin relative requests (`/api/...`); the native client passes
   * the absolute origin, e.g. `https://bunker.cispracs.ru`.
   */
  baseUrl?: string;
  /**
   * `fetch` implementation. Defaults to `globalThis.fetch`. Injectable for
   * tests and for runtimes that expose fetch under a different binding.
   */
  fetchImpl?: FetchLike;
  /**
   * Send cookies with every request (`credentials: 'include'`). This is how the
   * web authenticates. Native clients leave this off and use {@link getToken}.
   */
  withCredentials?: boolean;
  /**
   * Bearer-token provider for native clients. Called per request; may be sync
   * or async (e.g. an `expo-secure-store` read). When it returns a non-empty
   * string the request carries `Authorization: Bearer <token>`.
   */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /**
   * Called once when a request comes back 401. If it resolves `true` (e.g. a
   * native client silently rotated its refresh token), the original request is
   * retried EXACTLY once with a freshly-read {@link getToken}. Resolving `false`
   * lets the 401 propagate. Browsers omit this and re-auth via cispracs instead.
   * The handler must NOT itself issue requests through this same client on the
   * refresh path, or it can recurse — call the refresh endpoint directly.
   */
  onUnauthorized?: () => Promise<boolean>;
  /** Override the idempotency-nonce generator (defaults to {@link defaultGenerateNonce}). */
  generateNonce?: () => string;
  /**
   * Headers attached to every request. The native client sets
   * `X-Bunker-Client: native` here so the server's Origin-based CSRF guard can
   * recognise a caller that has no browser cookie jar to ride (a browser cannot
   * forge this header cross-origin — CORS is locked to the SPA origin). Web omits
   * it and keeps Origin-based CSRF. Per-call `init.headers` override these.
   */
  defaultHeaders?: Record<string, string>;
  /**
   * Send an empty JSON body (`{}` + `Content-Type: application/json`) on mutating
   * requests (POST/PUT/PATCH/DELETE) that have no body of their own. The browser's
   * `fetch` issues a well-formed body-less POST, but React Native's Android
   * networking layer (OkHttp) does not, and the server then rejects it with
   * `415 Unsupported Media Type`. Native clients enable this; web leaves it off so
   * its on-the-wire shape is unchanged. Per-call bodies always take precedence.
   */
  emptyBodyForMutations?: boolean;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Error thrown by {@link createRequest} on a non-2xx response. Carries the
 *  HTTP status and the parsed (or synthesised) error body. */
export interface RequestError extends Error {
  status: number;
  body: ApiErrorBody;
}

function joinUrl(baseUrl: string, path: string): string {
  if (!baseUrl) return path;
  // Avoid a doubled slash when baseUrl ends with '/' and path starts with '/'.
  if (baseUrl.endsWith('/') && path.startsWith('/')) return baseUrl + path.slice(1);
  return baseUrl + path;
}

/**
 * Build the `request<T>` function used by every domain module. Mirrors the old
 * web `request` exactly — JSON Content-Type only when a body is present, tolerant
 * parsing so an HTML error page doesn't crash the caller, status + body attached
 * to thrown errors — but with auth and base URL injected via {@link ApiClientConfig}.
 */
export function createRequest(config: ApiClientConfig = {}): RequestFn {
  const baseUrl = config.baseUrl ?? '';
  // Resolve `fetch` lazily per request when no explicit impl is injected, so a
  // runtime that swaps `globalThis.fetch` AFTER this client is constructed is
  // still honoured. This matters for the web `?mock=full` preview: `createApi()`
  // runs during module import (App pulls in the api facade), which is BEFORE
  // `installMockMode()` replaces `window.fetch` at boot — an eager capture here
  // would bind the original fetch and bypass the stub entirely. An explicitly
  // injected `fetchImpl` (tests, custom runtimes) is always used as-is.
  const resolveFetch = (): FetchLike => {
    if (config.fetchImpl) return config.fetchImpl;
    const globalFetch = (globalThis as { fetch?: FetchLike }).fetch;
    if (globalFetch) return globalFetch;
    throw new Error('No fetch implementation available; pass config.fetchImpl');
  };

  async function doRequest<T>(path: string, init: FetchRequestInit, allowRetry: boolean): Promise<T> {
    const hasBody = init.body !== undefined && init.body !== null;
    // Some runtimes (React Native / Android OkHttp) can't issue a well-formed
    // body-less mutating request, drawing a 415 from the server. When the client
    // opts in, give such requests an empty JSON body so they parse cleanly.
    const coerceEmptyBody =
      !!config.emptyBodyForMutations &&
      !hasBody &&
      MUTATING_METHODS.has((init.method ?? 'GET').toUpperCase());
    // FormData must NOT be labelled application/json: the runtime generates the
    // multipart boundary and writes the Content-Type itself, and overriding it
    // produces a body the server cannot parse — the request arrives with a
    // JSON content type and a multipart payload, and fails at the parser rather
    // than anywhere informative.
    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
    const sendsJson = (hasBody && !isFormData) || coerceEmptyBody;
    const headers: Record<string, string> = {
      ...(sendsJson ? { 'Content-Type': 'application/json' } : {}),
      ...(config.defaultHeaders ?? {}),
      ...(init.headers ?? {}),
    };

    if (config.getToken) {
      const token = await config.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const requestInit: FetchRequestInit = { ...init, headers };
    if (coerceEmptyBody) requestInit.body = '{}';
    if (config.withCredentials) requestInit.credentials = 'include';

    const res = await resolveFetch()(joinUrl(baseUrl, path), requestInit);
    const text = await res.text();

    // Body can be empty (204), JSON, or HTML (proxy/error pages). Guard the
    // parse so an upstream returning HTML on /api/* doesn't crash the caller.
    let body: unknown = undefined;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = { error: 'bad_response', raw: text.slice(0, 200) };
      }
    }

    if (!res.ok) {
      // One silent recovery attempt: on 401, let the caller refresh credentials
      // (token rotation) and replay the request once. getToken is re-read on the
      // retry, so the new token is picked up.
      if (res.status === 401 && allowRetry && config.onUnauthorized) {
        const recovered = await config.onUnauthorized();
        if (recovered) return doRequest<T>(path, init, false);
      }
      const err = (body ?? { error: 'http_error' }) as ApiErrorBody;
      throw Object.assign(new Error(err.message ?? err.error ?? `HTTP ${res.status}`), {
        status: res.status,
        body: err,
      });
    }
    return body as T;
  }

  return function request<T>(path: string, init: FetchRequestInit = {}): Promise<T> {
    return doRequest<T>(path, init, true);
  };
}

/**
 * Build a `?a=1&b=2` query string from key/value pairs, skipping `undefined`.
 * Hand-rolled with `encodeURIComponent` (core ES) so `@bunker/shared` needn't
 * pull in `lib.dom` for `URLSearchParams`. Returns '' when nothing is set.
 */
export function buildQuery(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * Opaque idempotency token for message sends. We only need uniqueness within
 * (channel, author); 16 random bytes is 128 bits of entropy, comically more
 * than necessary. Encoded as url-safe base64 without padding, by hand — Hermes
 * (React Native) has no `btoa`, and we want one implementation for every runtime.
 */
export function defaultGenerateNonce(): string {
  const bytes = new Uint8Array(16);
  const cryptoObj = (globalThis as { crypto?: { getRandomValues(a: Uint8Array): Uint8Array } })
    .crypto;
  if (!cryptoObj?.getRandomValues) {
    throw new Error('crypto.getRandomValues unavailable; cannot generate a client nonce');
  }
  cryptoObj.getRandomValues(bytes);

  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i]!;
    const b1 = i + 1 < bytes.length ? bytes[i + 1]! : undefined;
    const b2 = i + 2 < bytes.length ? bytes[i + 2]! : undefined;

    out += B64URL[b0 >> 2];
    out += B64URL[((b0 & 0b11) << 4) | ((b1 ?? 0) >> 4)];
    if (b1 === undefined) break;
    out += B64URL[((b1 & 0b1111) << 2) | ((b2 ?? 0) >> 6)];
    if (b2 === undefined) break;
    out += B64URL[b2 & 0b111111];
  }
  return out;
}
